import { zodResolver } from '@hookform/resolvers/zod'
import { assert } from '@sparkdotfi/common-universal'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { NormalizedSwapFormValues, SwapFormFields } from '../../types'
import { getSwapFormFields } from './getSwapFormFields'
import { getNormalizedFormValuesKey, normalizeFormValues } from './normalizeFormValues'
import { SwapFormSchema } from './schema'
import { useSwapValidator } from './useSwapValidator'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useDebounce } from '@/utils/useDebounce'

export interface UseSwapFormParams {
  tokenRepository: TokenRepository
  psmStables: TokenSymbol[] | undefined
}

export interface UseSwapFormResult {
  form: UseFormReturn<SwapFormSchema>
  formValues: NormalizedSwapFormValues
  isFormValid: boolean
  isDebouncing: boolean
  formFields: SwapFormFields
}

export function useSwapForm({ tokenRepository, psmStables }: UseSwapFormParams): UseSwapFormResult {
  assert(psmStables, 'PSM stables are not defined on this chain')
  assert(psmStables.length > 1, 'PSM stables should have at least 2 stables to be able to swap')
  const chainId = useChainId()
  const validator = useSwapValidator({ chainId, tokenRepository })

  const form = useForm<SwapFormSchema>({
    resolver: zodResolver(validator),
    defaultValues: {
      isMaxSelected: false,
      from: psmStables[0],
      to: psmStables[1],
      amount: '',
    },
  })
  const formFields = getSwapFormFields({
    form,
    tokenRepository,
    psmStables,
  })
  const formValues = normalizeFormValues({
    formValues: form.watch(),
    tokenRepository,
  })

  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getNormalizedFormValuesKey(formValues) + isFormValid.toString(),
  )

  return {
    form,
    formValues: debouncedValue.formValues,
    isFormValid,
    isDebouncing,
    formFields,
  }
}
